import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import FamiliaGrid from '@/components/feature/FamiliaGrid';
import { CATALOG_URL, waLink } from '@/config/site';
import { useSchemaLD } from '@/hooks/useSEO';
import { avisoImagens, avisoProjetos } from '@/mocks/curadoria';

const frentes = [
  {
    id: 'artesian',
    rotulo: 'Linha nova',
    titulo: 'Artesian — praças, alimentação e espaços coletivos',
    descricao:
      'Referência para ambientes de uso intensivo: cadeiras, banquetas, mesas, bancos, booths e complementos. Páginas 2 a 28 do catálogo 2026.',
    path: '/mobiliario-pracas-alimentacao',
    linkLabel: 'Praças de alimentação',
    secundario: { path: '/mobiliario-urbano', label: 'Mobiliário urbano' },
  },
  {
    id: 'marzo',
    rotulo: 'Marzo Vitorino',
    titulo: 'Altis, Térios e Vio — do executivo à operação',
    descricao:
      'Mesas diretivas, salas de reunião e estações de trabalho. Composições com armários no mesmo padrão de acabamento.',
    path: '/mesas',
    linkLabel: 'Ver mesas e estações',
    secundario: { path: '/marzo-vitorino', label: 'Sobre o fabricante' },
  },
  {
    id: 'plaxmetal',
    rotulo: 'Plaxmetal',
    titulo: 'Cadeiras corporativas para jornadas longas',
    descricao:
      'Bix X+, Darix X+, linha colaborativa, direção, poltronas Elyze e assentos modulares Rizoma.',
    path: '/cadeiras',
    linkLabel: 'Ver cadeiras',
    secundario: { path: '/plaxmetal', label: 'Sobre o fabricante' },
  },
  {
    id: 'gabbinetto',
    rotulo: 'Gabbinetto',
    titulo: 'Divisórias, cabines e silêncio',
    descricao:
      'Vidro simples, vidro duplo, divisória mista, portas, junta seca, cabines Booth e soluções acústicas.',
    path: '/divisorias',
    linkLabel: 'Ver divisórias',
    secundario: { path: '/cabines-acusticas', label: 'Cabines e acústica' },
  },
];

const passos = [
  { n: '01', t: 'Informe seu e-mail', d: 'O acesso começa em poucos passos.' },
  { n: '02', t: 'Crie uma senha', d: 'Defina sua senha para a área reservada.' },
  { n: '03', t: 'Solicite liberação', d: 'O acesso é liberado por um período.' },
  { n: '04', t: 'Explore a seleção', d: 'Navegue por produtos e soluções.' },
];

export default function ProdutosPage() {
  useSchemaLD({
    id: 'schema-produtos',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Catálogo visual de soluções — Home Office Premium',
      description:
        'Doze famílias de mobiliário corporativo, coletivo e institucional: cadeiras, mesas executivas, mesas de reunião, estações de trabalho, longarinas, auditórios, divisórias, cabines acústicas, soluções acústicas, mobiliário urbano, praças de alimentação e áreas de convivência.',
      url: 'https://homeofficepremium.com/produtos',
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-16">
        {/* HERO */}
        <section className="bg-white">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 pt-12 md:pt-20 pb-12 md:pb-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-end">
              <div className="lg:col-span-7">
                <p className="hop-eyebrow text-foreground-400">Soluções e catálogo</p>
                <h1 className="mt-5 text-[34px] sm:text-[46px] md:text-[58px] leading-[1.03] tracking-[-0.038em] font-semibold text-foreground-950 max-w-[760px]">
                  O mobiliário certo para cada ambiente.
                </h1>
                <p className="mt-5 md:mt-6 text-[16px] md:text-[18px] leading-[1.55] text-foreground-500 max-w-[560px]">
                  Doze famílias comerciais reunidas em uma curadoria única de sete catálogos de
                  fabricantes. Cada item traz fabricante, linha e aplicação recomendada.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                  <Link
                    to="/orcamento"
                    className="inline-flex items-center justify-center bg-foreground-950 text-white text-[14px] md:text-[15px] font-medium px-7 py-3.5 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Solicitar orçamento
                  </Link>
                  <a
                    href={waLink('corporativo')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-1.5 text-foreground-800 text-[14px] md:text-[15px] font-medium py-3 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-[15px]"></i>
                    Falar sobre o meu ambiente
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="border-t border-background-200 pt-5">
                  <p className="text-[12px] uppercase tracking-[0.14em] text-foreground-400">
                    Fabricantes nesta curadoria
                  </p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2.5">
                    {['Artesian', 'Marzo Vitorino', 'Plaxmetal', 'Gabbinetto'].map((marca) => (
                      <span
                        key={marca}
                        className="text-[14px] font-medium tracking-[-0.01em] text-foreground-800"
                      >
                        {marca}
                      </span>
                    ))}
                  </div>
                  <p className="mt-5 text-[12px] leading-[1.6] text-foreground-400 max-w-[320px]">
                    A marca Home Office Premium conduz a experiência. Os fabricantes aparecem como
                    identificação de produto, não como protagonistas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* GRID DE FAMÍLIAS */}
        <section className="bg-background-50 border-t border-background-200 py-14 md:py-20">
          <FamiliaGrid
            titulo="Catálogo visual por categoria"
            descricao="Imagens grandes, alinhamento preciso e identificação discreta, no padrão das grandes marcas de mobiliário internacional."
          />
        </section>

        {/* FRENTES COMERCIAIS */}
        <section className="bg-white border-t border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-20">
            <div className="max-w-[720px]">
              <p className="hop-eyebrow text-foreground-400">Frentes comerciais</p>
              <h2 className="mt-4 text-[26px] sm:text-[32px] md:text-[40px] leading-[1.08] tracking-[-0.036em] font-semibold text-foreground-950">
                Quatro frentes com catálogo próprio.
              </h2>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
              {frentes.map((frente) => (
                <article key={frente.id} className="border-t border-background-300 pt-5">
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-secondary-600">
                    {frente.rotulo}
                  </p>
                  <h3 className="mt-2.5 text-[19px] md:text-[21px] leading-[1.25] font-medium tracking-[-0.025em] text-foreground-950">
                    {frente.titulo}
                  </h3>
                  <p className="mt-2.5 text-[13.5px] leading-[1.65] text-foreground-500 max-w-[420px]">
                    {frente.descricao}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <Link
                      to={frente.path}
                      className="inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground-950 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {frente.linkLabel}
                      <i className="ri-arrow-right-line text-[12px]"></i>
                    </Link>
                    <Link
                      to={frente.secundario.path}
                      className="inline-flex items-center gap-1 text-[12.5px] text-foreground-400 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {frente.secundario.label}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CATÁLOGO PRIVADO */}
        <section className="bg-background-50 border-t border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-16 md:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
              <div className="lg:col-span-5">
                <h2 className="text-[26px] sm:text-[32px] md:text-[38px] leading-[1.1] tracking-[-0.035em] font-semibold text-foreground-950">
                  A seleção completa está no catálogo.
                </h2>
                <p className="mt-4 text-[14.5px] md:text-[15.5px] leading-[1.6] text-foreground-500 max-w-[420px]">
                  Modelos, configurações e detalhes técnicos em um ambiente reservado, com
                  acompanhamento de pedidos para clientes. É o mesmo catálogo de sempre — contas e
                  pedidos permanecem intactos.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/catalogo"
                    className="inline-flex items-center justify-center bg-foreground-950 text-white text-[14px] font-medium px-6 py-3 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Explorar catálogo
                  </Link>
                  <a
                    href={CATALOG_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 border border-background-300 text-foreground-800 text-[14px] font-medium px-6 py-3 rounded-full hover:border-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Já sou cliente
                    <i className="ri-arrow-right-up-line text-[13px]"></i>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="border-t border-background-200">
                  {passos.map((step) => (
                    <div key={step.n} className="flex items-start gap-5 py-5 border-b border-background-200">
                      <span className="text-[11px] font-medium tracking-[0.14em] text-foreground-300 mt-1">
                        {step.n}
                      </span>
                      <div>
                        <p className="text-[15px] font-medium text-foreground-950 tracking-[-0.02em]">
                          {step.t}
                        </p>
                        <p className="mt-1 text-[13px] text-foreground-500 leading-relaxed">
                          {step.d}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AVISOS */}
        <section className="bg-white border-t border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-10 md:py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              <p className="flex items-start gap-2.5 text-[11.5px] leading-[1.65] text-foreground-400">
                <i className="ri-image-line mt-[1px] text-[14px] text-foreground-300"></i>
                <span>{avisoImagens}</span>
              </p>
              <p className="flex items-start gap-2.5 text-[11.5px] leading-[1.65] text-foreground-400">
                <i className="ri-shield-check-line mt-[1px] text-[14px] text-foreground-300"></i>
                <span>{avisoProjetos}</span>
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-20 md:pb-28 bg-white">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10">
            <div className="rounded-[20px] md:rounded-[28px] bg-foreground-950 px-6 sm:px-10 md:px-16 py-12 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <h2 className="text-[24px] sm:text-[30px] md:text-[34px] leading-[1.12] tracking-[-0.035em] font-semibold text-background-50 max-w-[520px]">
                  Precisa de ajuda para escolher?
                </h2>
                <p className="mt-3 text-[14px] text-background-50/55 max-w-[440px] leading-relaxed">
                  Conte o que o seu ambiente exige e receba uma recomendação objetiva.
                </p>
              </div>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center bg-background-50 text-foreground-950 text-[14px] font-medium px-7 py-3.5 rounded-full hover:bg-white transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                Falar com a equipe
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}